import { useState, useEffect, useCallback } from "react";
import "../styles/Settings.css";
import Fetch from "../services/fetchService"; 
import AuthData from '../common/AuthData';
import { ORDERS_API } from '../common/ResourcePaths'; // Import the constants
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import OrderView from './OrderView'; // Import the OrderView component
import ReusableDataGrid from '../common/components/ReusableDataGrid'; // Import the generic ReusableDataGrid component
import { createOrdersColumns } from '../common/utils/gridHelpers'; // Import the orders columns helper
import { CANCELLED, COMPLETED, NEW, PROCESSING, DELIVERING } from '../common/OrderStatus'; // Import the correct constants
import Feedback from '../common/components/Feedback';

function Orders() {
   
    const [activeButton, setActiveButton] = useState(NEW); // State to track active button
    const [orders, setOrders] = useState([]); // State to store fetched orders
    const [loader, setLoader] = useState(false); // State for loader visibility
    const [error, setError] = useState(''); // State to manage error messages
    const [pageSize, setPageSize] = useState(1000); // Default page size
    const [page, setPage] = useState(1); // State to manage page
    const [rowCount, setRowCount] = useState(0); // State to manage total row count
    const [selectedOrderId, setSelectedOrderId] = useState(null); // State to track selected order ID

    const getOrders = useCallback(async () => {
        try {
            setLoader(true);
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${ORDERS_API}?code=${authData.azureFunctionKey}&page=${page}&pageSize=${pageSize}&status=${activeButton}`; // Added status parameter
            const response = await Fetch(url, authData.clientId);
            setOrders(response.data); // Update orders with the current page's data
            setRowCount(response.totalCount); // Ensure rowCount is updated with the total number of records (e.g., 20)
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to fetch orders. Please try again later.");
        } finally {
            setLoader(false);
        }
    }, [page, pageSize, activeButton]);

    useEffect(() => {
        getOrders();
    }, [getOrders]); // Refetch data when getOrders changes

    const handleButtonClick = (buttonName) => {
        setActiveButton(buttonName); // Set the clicked button as active
        setSelectedOrderId(null); // Reset selected order ID to close OrderView
    };

    useEffect(() => {
        handleButtonClick(NEW);
    }, []);

    const handlePreviewClick = (orderId) => {
        setSelectedOrderId(orderId); // Set the selected order ID to display OrderView
    };

    const closeOrderView = (status, shouldRefresh = true) => {
        setActiveButton(status); // Set the active button to the status of the order
        setSelectedOrderId(null); // Reset selected order ID to close OrderView
        // Refresh the orders data to get the newest information
        if (shouldRefresh) {
            getOrders();
        }
    }

    return (
        <div className="settings">
                <div className="settings-buttons">
                    <button
                        className={`settings-button ${activeButton === NEW ? "active" : ""}`}
                        onClick={() => handleButtonClick(NEW)}
                    >
                        {NEW}
                    </button>
                    <button
                        className={`settings-button ${activeButton === PROCESSING ? "active" : ""}`}
                        onClick={() => handleButtonClick(PROCESSING)}
                    >
                        {PROCESSING}
                    </button>
                     <button
                        className={`settings-button ${activeButton === DELIVERING ? "active" : ""}`}
                        onClick={() => handleButtonClick(DELIVERING)}
                    >
                        {"Delivering"}
                    </button>
                    <button
                        className={`settings-button ${activeButton === COMPLETED ? "active" : ""}`}
                        onClick={() => handleButtonClick(COMPLETED)}
                    >
                        {COMPLETED}
                    </button>
                    <button
                        className={`settings-button ${activeButton === CANCELLED ? "active" : ""}`}
                        onClick={() => handleButtonClick(CANCELLED)}
                    >
                        {CANCELLED}
                    </button>
                </div>

            <div style={{ flex: 1, position: 'relative' }}>
                {selectedOrderId ? (
                    <OrderView 
                        id={selectedOrderId} 
                        closeOrderView={closeOrderView}
                        /> 
                ) : (
                    <>
                        {loader && <RelativeSvgLoader />}
                        <ReusableDataGrid
                            rows={orders}
                            columns={createOrdersColumns(handlePreviewClick)}
                            pageSize={pageSize}
                            page={page}
                            rowCount={rowCount}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            pageSizeOptions={[5, 10, 25, 50]}
                            initialPageSize={5}
                            loading={false}
                            error={error}
                            errorMessage="Failed to fetch orders. Please try again later."
                            loadingComponent={<RelativeSvgLoader />}
                        />
                    </>
                )}
            </div>
            
            {/* Feedback system available on orders page */}
            <Feedback />
        </div>
    );
}

export default Orders;